import React from 'react'

import { actionLabelMap, IActionSettings, IActionType } from '@makyoapp/makyo-common'
import { Modal, RenderActionTypeIcon } from '@makyoapp/makyo-ui'

import { ActFormPlugin, ActionFormMap } from '@/context/ActionFormProviderV2'
import { isEmpty } from '@/shared/utils'

import { WorkflowFormData } from './types'

interface Option {
  id: string
  label: string
  description?: string
}

interface WorkflowActionFormModalProps {
  show: boolean
  onHide: () => void
  onSubmit: (id: string, action: IActionType, data?: IActionSettings, meta?: Record<string, any>) => void
  editedForm: WorkflowFormData | null
  setEditedForm: (form: WorkflowFormData | null) => void
  actionOptions?: Option[]
  nodeContext?: {
    currentNode: any | null
    previousNode: any | null
    nextNode: any | null
    previousNodes: any[]
    nextNodes: any[]
    isFirstNode: boolean
    isLastNode: boolean
    isTriggerNode: boolean
  } | null
}

// Simple context for workflow node information
interface WorkflowNodeContextType {
  currentNode: any | null
  previousNode: any | null
  nextNode: any | null
  isFirstNode: boolean
  isLastNode: boolean
  isTriggerNode: boolean
  allActions: Option[]
}

const WorkflowNodeContext = React.createContext<WorkflowNodeContextType | null>(null)

export const useWorkflowNodeContext = () => {
  const context = React.useContext(WorkflowNodeContext)
  return context
}

const LoadingPlaceholder = () => (
  <div className='space-y-4 animate-pulse'>
    <div className='p-4'>
      <div className='flex gap-6'>
        <div className='w-[25%] h-[36px] rounded-sm bg-zinc-300 dark:bg-slate-700' />
        <div className='w-full h-[36px] rounded-sm bg-zinc-300 dark:bg-slate-700' />
      </div>
    </div>
    <div className='px-4 py-2 border-t flex justify-between items-center'>
      <div className='w-[80px] h-[36px] rounded-sm bg-zinc-300 dark:bg-slate-700' />
      <div className='w-[80px] h-[36px] rounded-sm bg-zinc-300 dark:bg-slate-700' />
    </div>
  </div>
)

export const WorkflowActionFormModal: React.FC<WorkflowActionFormModalProps> = ({
  show,
  onHide,
  onSubmit,
  editedForm,
  setEditedForm,
  actionOptions,
  nodeContext,
}) => {
  const ActionFormComponent = editedForm ? ActionFormMap[editedForm.type] : null

  if (!show || !editedForm) {
    return null
  }

  const isPluginAction = editedForm?.settings?.pluginAction
  const Component = isPluginAction ? ActFormPlugin : ActionFormComponent
  const contextValue: WorkflowNodeContextType = {
    currentNode: nodeContext?.currentNode || null,
    previousNode: nodeContext?.previousNode || null,
    nextNode: nodeContext?.nextNode || null,
    isFirstNode: nodeContext?.isFirstNode || false,
    isLastNode: nodeContext?.isLastNode || false,
    isTriggerNode: nodeContext?.isTriggerNode || false,
    allActions: actionOptions || [],
  }

  return (
    <React.Suspense fallback={null}>
      <Modal fix show={show} onHide={onHide} closeOnClickOutside={false} innerClassName='max-w-[98vw]!'>
        <Modal.Header onCloseClick={onHide}>
          <div className='flex items-center gap-2 py-2'>
            <RenderActionTypeIcon type={editedForm?.type} className='rounded-xs' />
            <span>
              {actionLabelMap[editedForm?.type.toString() as keyof typeof actionLabelMap] ||
                editedForm?.type.toString()}
            </span>
          </div>
        </Modal.Header>
        <Modal.Body noPadding className='min-w-[768px]'>
          <WorkflowNodeContext.Provider value={contextValue}>
            <React.Suspense fallback={<LoadingPlaceholder />}>
              <div>
                {Component && (
                  <Component
                    columnDataTypes={['profile', 'single-cell', 'session-id']}
                    onSubmit={(data, meta) => {
                      console.log('meta', meta)
                      if (!editedForm) return
                      if (!!data && !isEmpty(data)) {
                        onSubmit(editedForm.id, editedForm.type, data, meta)
                        if (!meta?.preventClose) {
                          setEditedForm(null)
                        }
                      }
                    }}
                    onDismiss={onHide}
                    initialData={{
                      ...editedForm.settings,
                      actionOptions,
                      isActionWorkflow: true,
                    }}
                    triggerSource='component'
                  />
                )}
              </div>
            </React.Suspense>
          </WorkflowNodeContext.Provider>
        </Modal.Body>
      </Modal>
    </React.Suspense>
  )
}
